import { ArrowLeft } from 'lucide-react'

function Bone({ className = '', style }) {
  const classes = className ? `patients-skel ${className}` : 'patients-skel'
  return <span className={classes} style={style} aria-hidden="true" />
}

const CARD_NAME_WIDTHS = ['9.5rem', '7.6rem', '11rem', '6.8rem', '8.8rem', '8.2rem']

export function PatientsKpiSkeleton() {
  return (
    <div className="finance-dashboard-kpi-grid" aria-hidden="true">
      {Array.from({ length: 4 }, (_, index) => (
        <article key={index} className="patients-skel-kpi" style={{ '--skel-delay': `${index * 0.08}s` }}>
          <div className="patients-skel-kpi-head">
            <Bone className="patients-skel--kicker" />
            <Bone className="patients-skel--icon" />
          </div>
          <Bone className="patients-skel--kpi-value" />
          <Bone className="patients-skel--kpi-hint" />
        </article>
      ))}
    </div>
  )
}

export function PatientsCardsSkeleton() {
  return (
    <div className="patients-grid patients-grid--cards" aria-hidden="true">
      {CARD_NAME_WIDTHS.map((width, index) => (
        <article
          key={width}
          className="patients-card patients-card--skeleton"
          style={{ '--skel-delay': `${index * 0.07}s` }}
        >
          <div>
            <div className="patients-card-top">
              <div className="patients-identity">
                <Bone className="patients-skel--avatar" />
                <div className="patients-skel-copy">
                  <Bone className="patients-skel--kicker" />
                  <Bone className="patients-skel--name" style={{ width }} />
                  <Bone className="patients-skel--muted" />
                </div>
              </div>
              <Bone className="patients-skel--badge" />
            </div>
            <div className="patients-meta">
              <Bone className="patients-skel--meta" />
              <Bone className="patients-skel--meta patients-skel--meta-short" />
            </div>
            <div className="patients-vitals patients-vitals--skeleton">
              <div className="patients-vitals-head">
                <Bone className="patients-skel--tiny" />
                <Bone className="patients-skel--tiny patients-skel--tiny-short" />
              </div>
              <div className="patients-vitals-grid">
                {Array.from({ length: 4 }, (_, vital) => (
                  <div key={vital}><Bone className="patients-skel--vital" /></div>
                ))}
              </div>
            </div>
          </div>
          <div className="patients-card-actions">
            <Bone className="patients-skel--btn patients-skel--btn-sm" />
            <Bone className="patients-skel--btn" />
          </div>
        </article>
      ))}
    </div>
  )
}

const TAB_WIDTHS = ['8.2rem', '8.8rem', '6.4rem', '7.2rem', '7.6rem', '8.6rem']
const TABLE_COLUMNS = ['1.5fr', '0.7fr', '0.7fr', '0.7fr', '0.7fr', '0.9fr', '0.6fr', '0.6fr', '1.3fr']

export function PatientRecordSkeleton({ onBack }) {
  return (
    <div className="finance-dashboard patients-module" aria-busy="true" aria-live="polite">
      <p className="patients-skel-sr">Carregando prontuário</p>

      <div className="finance-dashboard-header">
        <div>
          <Bone className="patients-skel--title" />
          <Bone className="patients-skel--subtitle" />
        </div>
        <div className="finance-dashboard-header-actions">
          <button type="button" onClick={onBack} className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive">
            <ArrowLeft size={14} /> Voltar
          </button>
          <Bone className="patients-skel--btn patients-skel--btn-ghost" />
        </div>
      </div>

      <section className="patients-panel" aria-hidden="true">
        <div className="patients-profile">
          <div className="patients-profile-main">
            <Bone className="patients-skel--avatar-lg" />
            <div className="patients-skel-copy">
              <div className="patients-skel-row-inline">
                <Bone className="patients-skel--badge" />
                <Bone className="patients-skel--badge patients-skel--badge-short" />
                <Bone className="patients-skel--muted patients-skel--inline" />
              </div>
              <Bone className="patients-skel--name-lg" />
              <Bone className="patients-skel--line" />
              <div className="patients-meta patients-meta--skeleton">
                <Bone className="patients-skel--meta" />
                <Bone className="patients-skel--meta patients-skel--meta-mid" />
                <Bone className="patients-skel--meta patients-skel--meta-short" />
              </div>
            </div>
          </div>
          <div className="patients-profile-actions">
            <Bone className="patients-skel--btn" />
            <Bone className="patients-skel--btn patients-skel--btn-mid" />
            <Bone className="patients-skel--btn" />
          </div>
        </div>

        <div className="patients-callouts">
          <div className="patients-callout patients-callout--rose">
            <Bone className="patients-skel--icon-sm" />
            <div className="patients-skel-copy">
              <Bone className="patients-skel--kicker" />
              <div className="patients-skel-row-inline">
                <Bone className="patients-skel--pill" />
                <Bone className="patients-skel--pill patients-skel--pill-short" />
              </div>
            </div>
          </div>
          <div className="patients-callout patients-callout--amber">
            <Bone className="patients-skel--icon-sm" />
            <div className="patients-skel-copy">
              <Bone className="patients-skel--kicker" />
              <div className="patients-skel-row-inline">
                <Bone className="patients-skel--pill patients-skel--pill-wide" />
              </div>
            </div>
          </div>
        </div>

        <div className="patients-tabs">
          {TAB_WIDTHS.map((width, index) => (
            <Bone key={width} className="patients-skel--tab" style={{ width, '--skel-delay': `${0.12 + index * 0.05}s` }} />
          ))}
        </div>
      </section>

      <div aria-hidden="true">
        <div className="patients-section-head">
          <div className="patients-skel-copy">
            <Bone className="patients-skel--heading" />
            <Bone className="patients-skel--muted patients-skel--block" />
          </div>
          <Bone className="patients-skel--btn patients-skel--btn-mid" />
        </div>

        <div className="patients-metrics patients-metrics--6">
          {Array.from({ length: 6 }, (_, index) => (
            <article key={index} className="patients-metric" style={{ '--skel-delay': `${index * 0.05}s` }}>
              <div className="patients-metric-label">
                <Bone className="patients-skel--kicker" />
                <Bone className="patients-skel--icon-sm" />
              </div>
              <Bone className="patients-skel--metric-value" />
            </article>
          ))}
        </div>

        <div className="patients-table-card">
          <div className="patients-table-head">
            <div className="patients-skel-copy">
              <Bone className="patients-skel--heading" />
              <Bone className="patients-skel--muted patients-skel--block" style={{ width: '18rem' }} />
            </div>
          </div>
          <div className="patients-table-scroll">
            <div className="patients-skel-table">
              <div className="patients-skel-table-row patients-skel-table-row--head" style={{ gridTemplateColumns: TABLE_COLUMNS.join(' ') }}>
                {TABLE_COLUMNS.map((column, columnIndex) => (
                  <Bone key={`head-${columnIndex}-${column}`} />
                ))}
              </div>
              {Array.from({ length: 5 }, (_, row) => (
                <div
                  key={row}
                  className="patients-skel-table-row"
                  style={{ gridTemplateColumns: TABLE_COLUMNS.join(' '), '--skel-delay': `${row * 0.06}s` }}
                >
                  {TABLE_COLUMNS.map((column, columnIndex) => (
                    <Bone
                      key={`${row}-${columnIndex}`}
                      style={{ width: columnIndex === 0 ? '86%' : '70%' }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
